// Afstandsportaal — het contract met de platform-api.
//
// Het platform stuurt alles door naar de backoffice van het asiel en bewaart er niets van; deze
// module praat alleen met het platform. De sessie is een token dat de portaal-inlog teruggeeft; die
// rijdt mee in een eigen header en is geen bearer-token van de app — hij opent precies één deur.
//
// We hergebruiken de Refine-dataProvider voor de JSON-aanroepen, zodat de basis-URL, de dev-proxy en
// de fouttypering gelijk lopen met de rest van de app. De upload gaat eromheen: die is multipart.

import type { HttpError } from "@refinedev/core";

import { dataProvider } from "../dataProvider";

/** Het besturingselement dat bij een vraag hoort; het asiel bepaalt per veld welk het is. */
export type AfstandVraagType =
  | "scale"
  | "single"
  | "multi"
  | "yesno"
  | "text"
  | "number"
  | "date"
  | "photo";

/** Eén vraag, zoals het asiel hem heeft opgesteld. */
export interface AfstandVraag {
  /** De sleutel waaronder het antwoord terugkomt. */
  id: string;
  /** De groep waarin hij staat, bijvoorbeeld "Gedrag en omgang". */
  groep: string;
  /** Waar op de eerste vraag van een groep, zodat het portaal er een kop boven kan zetten. */
  groepStart: boolean;
  type: AfstandVraagType;
  vraag: string;
  toelichting: string | null;
  verplicht: boolean;
  keuzes: string[];
}

export interface AfstandVragenlijst {
  wizardDefinitieId: string;
  naam: string | null;
  vragen: AfstandVraag[];
}

export interface AfstandDiersoort {
  id: number;
  naam: string;
}

/**
 * De beginpagina.
 *
 * <p>`mogelijk` is onwaar wanneer het asiel nog niets heeft ingericht of zijn vestiging niet
 * meestuurt. Dat is geen fout: het portaal zegt dan dat aanmelden hier nog niet kan.
 */
export interface AfstandStart {
  opvangNaam: string;
  mogelijk: boolean;
  diersoorten: AfstandDiersoort[];
}

const basis = (opvangSleutel: string) => `public/afstand/${encodeURIComponent(opvangSleutel)}`;

/**
 * De sessie rijdt mee in een eigen header.
 *
 * <p>Geen bearer-token van de app: deze sessie opent precies één deur -- de eigen aanvraag bij deze
 * opvang. Hem als bearer meesturen zou suggereren dat hij ergens anders ook iets betekent.
 *
 * <p>Zonder deze header is een geraden aanvraag-sleutel genoeg om in andermans dossier te schrijven;
 * de sleutel wijst de aanvraag aan, de sessie bewijst dat jij het bent.
 */
const sessie = (token: string) => ({ "X-Afstand-Sessie": token });

/**
 * Zoekparameters in de url zetten.
 *
 * <p>De gedeelde dataProvider kent alleen url, method en payload -- een `query` die je meegeeft
 * wordt zonder morren weggegooid. Dat is stil kapot: de vragenlijst haalde daardoor de algemene
 * lijst op in plaats van die van de diersoort, zonder dat er iets misging dat je kon zien.
 */
function met(pad: string, parameters: Record<string, string | number | undefined>): string {
  const delen = Object.entries(parameters)
    .filter(([, v]) => v !== undefined && String(v) !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return delen.length === 0 ? pad : `${pad}?${delen.join("&")}`;
}

export async function haalStart(opvangSleutel: string): Promise<AfstandStart> {
  const result = await dataProvider.custom!<AfstandStart>({ url: basis(opvangSleutel), method: "get" });
  return result.data;
}

/**
 * Een inlogcode aanvragen.
 *
 * <p>Geeft op test en acceptatie de code terug, omdat daar geen mail uitgaat. In productie is dat
 * altijd leeg en komt de code per mail.
 */
export async function vraagCode(opvangSleutel: string, email: string): Promise<string | null> {
  const result = await dataProvider.custom!<{ devCode: string | null }>({
    url: `${basis(opvangSleutel)}/code`,
    method: "post",
    payload: { email },
  });
  return result.data?.devCode ?? null;
}

/** De code nakijken. Gooit een HttpError met 401 als hij niet klopt of verlopen is. */
export async function verifieerCode(
  opvangSleutel: string,
  email: string,
  code: string,
): Promise<string> {
  const result = await dataProvider.custom!<{ token: string }>({
    url: `${basis(opvangSleutel)}/code/verifieer`,
    method: "post",
    payload: { email, code },
  });
  return result.data.token;
}

/** De rassen die het asiel kent voor deze diersoort; leeg is een geldig antwoord. */
/** Een ras met de diersoort waar het bij hoort; leeg bij een los ingevoerd ras. */
export interface AfstandRas {
  id: number;
  naam: string;
  diersoortId: number | null;
}

/**
 * De rassen van een opvang, van een diersoort of van allemaal.
 *
 * <p>Zonder {@code diersoortId} komen ze allemaal, elk met de soort waar het bij hoort. Zo kan de
 * eigenaar op ras zoeken zonder eerst de diersoort te kiezen -- wie "Labrador" intikt weet wel dat
 * het een hond is -- en vult het formulier die daarna zelf in.
 */
export async function haalRassen(
  opvangSleutel: string,
  diersoortId?: number,
): Promise<AfstandRas[]> {
  const result = await dataProvider.custom!<AfstandRas[]>({
    url: diersoortId == null
      ? `${basis(opvangSleutel)}/rassen`
      : met(`${basis(opvangSleutel)}/rassen`, { diersoortId }),
    method: "get",
  });
  return Array.isArray(result.data) ? result.data : [];
}

export interface AfstandAdres {
  straat: string | null;
  woonplaats: string | null;
}

/**
 * Straat en woonplaats bij een postcode en huisnummer.
 *
 * <p>Beide leeg = onbekende combinatie. Dat is geen fout: een postbusadres of een nieuwbouwstraat
 * bestaat ook, en dan vult de eigenaar het gewoon zelf in.
 */
export async function zoekAdres(
  opvangSleutel: string,
  postcode: string,
  huisnummer: string,
  toevoeging?: string,
): Promise<AfstandAdres> {
  const result = await dataProvider.custom!<AfstandAdres>({
    url: met(`${basis(opvangSleutel)}/adres`, { postcode, huisnummer, toevoeging }),
    method: "get",
  });
  return result.data ?? { straat: null, woonplaats: null };
}

/** Een voorgestelde enting uit een paspoortfoto; de eigenaar bevestigt of past hem aan. */
export interface AfstandEntingVoorstel {
  vaccin: string | null;
  datum: string | null;
  geldigTot: string | null;
}

export interface AfstandScan {
  beschikbaar: boolean;
  melding: string | null;
  entingen: AfstandEntingVoorstel[];
}

/** De paspoortfoto's laten lezen. Multipart, dus buiten de JSON-dataProvider om. */
export async function scanPaspoort(opvangSleutel: string, bestanden: File[]): Promise<AfstandScan> {
  const formulier = new FormData();
  for (const bestand of bestanden) {
    formulier.append("files", bestand);
  }
  const antwoord = await fetch(
    `/api/public/afstand/${encodeURIComponent(opvangSleutel)}/paspoort-scan`,
    { method: "POST", body: formulier },
  );
  if (!antwoord.ok) {
    return { beschikbaar: false, melding: "Het uitlezen lukte niet", entingen: [] };
  }
  return (await antwoord.json()) as AfstandScan;
}

/** Wat er van de voorkant van het paspoort gelezen is; elk veld kan leeg zijn. */
export interface AfstandDierScan {
  beschikbaar: boolean;
  melding: string | null;
  naam: string | null;
  chipnummer: string | null;
  geboortedatum: string | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
}

/**
 * De voorkant van het paspoort laten lezen.
 *
 * <p>Het antwoord is een voorstel: het vult de velden in die nog leeg zijn en laat staan wat de
 * eigenaar al zelf heeft ingevuld. Zijn eigen invoer overschrijven met een gok is erger dan niets
 * invullen.
 */
export async function scanPaspoortDier(
  opvangSleutel: string,
  bestanden: File[],
): Promise<AfstandDierScan> {
  const formulier = new FormData();
  for (const bestand of bestanden) {
    formulier.append("files", bestand);
  }
  const antwoord = await fetch(
    `/api/public/afstand/${encodeURIComponent(opvangSleutel)}/paspoort-dier`,
    { method: "POST", body: formulier },
  );
  if (!antwoord.ok) {
    return {
      beschikbaar: false, melding: "Het uitlezen lukte niet", naam: null, chipnummer: null,
      geboortedatum: null, diersoort: null, ras: null, geslacht: null,
    };
  }
  return (await antwoord.json()) as AfstandDierScan;
}

export interface AfstandContact {
  naam: string;
  email: string;
  telefoon?: string;
  straat?: string;
  huisnummer?: string;
  postcode?: string;
  woonplaats?: string;
}

/** De vragenlijst van deze diersoort; leeg wanneer het asiel er geen heeft ingericht (204). */
export async function haalVragenlijst(
  opvangSleutel: string,
  diersoort: string,
): Promise<AfstandVragenlijst | null> {
  // 204 bij een opvang die voor deze diersoort niets heeft ingericht; dan is `data` leeg en is dat
  // het antwoord, geen fout.
  const result = await dataProvider.custom!<AfstandVragenlijst>({
    url: met(`${basis(opvangSleutel)}/vragenlijst`, { diersoort }),
    method: "get",
  });
  return result.data?.wizardDefinitieId ? result.data : null;
}

/**
 * Welke locatie een aanvraag van deze postcode krijgt.
 *
 * <p>Precies een naam, nooit een lijst -- de eigenaar mag niet zien welke locaties er zijn. Hij
 * wordt vooraf opgevraagd zodat de controlestap dezelfde naam noemt als de bevestiging erna; zonder
 * die vraag zou daar de locatie uit de link staan, en dat is lang niet altijd dezelfde.
 *
 * <p>Leeg wanneer de postcode niet te plaatsen is. Het scherm noemt dan geen naam, want een naam
 * noemen die straks niet klopt is erger dan er geen noemen.
 */
export async function haalVestiging(opvangSleutel: string, postcode: string): Promise<string | null> {
  const result = await dataProvider.custom!<{ naam: string | null }>({
    url: met(`${basis(opvangSleutel)}/vestiging`, { postcode }),
    method: "get",
  });
  return result.data?.naam ?? null;
}

/**
 * Wat het asiel teruggeeft als de aanvraag opengaat.
 *
 * <p>`vestigingNaam` is de locatie die hem behandelt. Die kiest het asiel zelf, op de postcode van
 * de eigenaar; het portaal stuurt een adres en krijgt één naam terug. Nooit een lijst om uit te
 * kiezen -- wie de locaties kent belt de volgende zodra het antwoord hem niet zint.
 */
export interface AfstandGeopend {
  aanvraagId: string;
  vestigingNaam: string | null;
}

/** De aanvraag openen. Het asiel geeft de sleutel uit; die houdt het portaal in zijn geheugen. */
export async function openAanvraag(
  opvangSleutel: string,
  token: string,
  contact: AfstandContact,
  wizardDefinitieId: string | null,
): Promise<AfstandGeopend> {
  const result = await dataProvider.custom!<AfstandGeopend>({
    url: basis(opvangSleutel),
    method: "post",
    headers: sessie(token),
    payload: { contact, wizardDefinitieId },
  });
  return result.data;
}

export interface AfstandDier {
  naam: string | null;
  diersoortId: number | null;
  /**
   * Het ras zoals het asiel het kent.
   *
   * <p>Een verwijzing en geen tekst: het asiel bewaart het ras uit zijn eigen lijst. Ontbrak dit veld,
   * dan kwam het dier zonder ras binnen en stond er in het dossier een streepje terwijl de eigenaar
   * het wel had ingevuld. Leeg bij een vrij ingetikt ras.
   */
  rasId: number | null;
  geslacht: string | null;
  geboortedatum: string | null;
  chipPasnummer: string | null;
  gecastreerd: boolean | null;
  antwoordenJson: string | null;
  /** De vaccinaties die de eigenaar na controle bevestigde; eigen veld, niet bij de antwoorden. */
  entingenJson: string | null;
}

/** Altijd de hele set dieren: het formulier stuurt een momentopname, geen wijziging. */
export async function bewaarDieren(
  aanvraagId: string,
  token: string,
  dieren: AfstandDier[],
): Promise<void> {
  await dataProvider.custom!({
    url: `public/afstand/aanvraag/${aanvraagId}/dieren`,
    method: "put",
    headers: sessie(token),
    payload: { dieren },
  });
}

/** Hoe het met de lopende aanvraag staat; leeg als er nog geen aanvraag is. */
export interface AfstandStand {
  aanvraagId: string;
  status: string;
  /** Afgesloten betekent dat de toegang tot deze aanvraag is beeindigd. */
  afgesloten: boolean;
  /**
   * De locatie die de aanvraag nu behandelt.
   *
   * <p>Elke keer opnieuw opgehaald en niet onthouden: het asiel kan een aanvraag verplaatsen, en dan
   * hoort hier de locatie te staan die hem nu echt heeft.
   */
  vestigingNaam: string | null;
  /**
   * Heeft een medewerker om aanvulling gevraagd?
   *
   * <p>Niet hetzelfde als de status ACTIE_VEREIST: die zet het asiel ook vanzelf zodra een dossier
   * niet compleet is. Dan heeft niemand iets gevraagd, en de eigenaar hoort niet te lezen dat dat
   * wel zo is.
   */
  aanvullingGevraagd: boolean;
}

export async function haalStand(token: string): Promise<AfstandStand | null> {
  const result = await dataProvider.custom!<AfstandStand>({
    url: "public/afstand/aanvraag/stand",
    method: "get",
    headers: sessie(token),
  });
  return result.data?.aanvraagId ? result.data : null;
}

export async function dienIn(aanvraagId: string, token: string): Promise<void> {
  await dataProvider.custom!({
    url: `public/afstand/aanvraag/${aanvraagId}/indienen`,
    method: "post",
    headers: sessie(token),
    payload: {},
  });
}

/** Een bijlage. Multipart, dus buiten de JSON-dataProvider om. */
/**
 * Een bijlage versturen.
 *
 * <p>{@code dierVolgnummer} is het hoeveelste dier het is, in dezelfde volgorde waarin ze net zijn
 * weggeschreven; {@code soort} zegt wat voor bestand het is. Zonder die twee komt elk bestand los bij
 * de aanvraag te hangen, en dan moet een medewerker bij drie dieren zelf uitzoeken welk paspoort bij
 * welk dier hoort.
 */
export async function uploadDocument(
  aanvraagId: string,
  token: string,
  bestand: File,
  dierVolgnummer?: number,
  soort?: string,
): Promise<void> {
  const formulier = new FormData();
  formulier.append("file", bestand);
  const vraag = new URLSearchParams();
  if (dierVolgnummer != null) vraag.set("dierVolgnummer", String(dierVolgnummer));
  if (soort) vraag.set("soort", soort);
  const staart = vraag.toString() ? `?${vraag}` : "";
  const antwoord = await fetch(`/api/public/afstand/aanvraag/${aanvraagId}/documenten${staart}`, {
    method: "POST",
    headers: sessie(token),
    body: formulier,
  });
  if (!antwoord.ok) {
    const fout: HttpError = {
      message: antwoord.status === 413 ? "Dit bestand is te groot" : "Het bestand kon niet worden verstuurd",
      statusCode: antwoord.status,
    };
    throw fout;
  }
}

/** Een bijlage die al bij het asiel ligt. */
export interface AfstandBijlage {
  documentId: string;
  /** Het hoeveelste dier, in de volgorde waarin ze zijn weggeschreven; leeg = bij de aanvraag. */
  dierVolgnummer: number | null;
  soort: string | null;
  bestandsnaam: string;
  contentType: string | null;
  grootte: number | null;
}

/**
 * Wat de eigenaar al heeft verstuurd.
 *
 * <p>Alleen de koppen. De bytes van tien foto's in één antwoord proppen maakt het terugkijken
 * langzaam voor wie alleen wil weten óf zijn foto aankwam.
 */
export async function haalBijlagen(aanvraagId: string, token: string): Promise<AfstandBijlage[]> {
  const result = await dataProvider.custom!<AfstandBijlage[]>({
    url: `public/afstand/aanvraag/${aanvraagId}/documenten`,
    method: "get",
    headers: sessie(token),
  });
  return result.data ?? [];
}

/**
 * De bytes van één bijlage, als url om te tonen.
 *
 * <p>Via fetch en niet rechtstreeks in een {@code src}: de bijlage zit achter de sessie van deze
 * eigenaar, en een {@code <img>} kan die kopregel niet meesturen. De url hoort bij dit tabblad; wie
 * hem maakt geeft hem ook weer vrij.
 */
export async function haalBijlageUrl(aanvraagId: string, documentId: string,
                                     token: string): Promise<string> {
  const antwoord = await fetch(
    `/api/public/afstand/aanvraag/${aanvraagId}/documenten/${documentId}`,
    { headers: sessie(token) },
  );
  if (!antwoord.ok) {
    const fout: HttpError = {
      message: "De bijlage kon niet worden opgehaald",
      statusCode: antwoord.status,
    };
    throw fout;
  }
  return URL.createObjectURL(await antwoord.blob());
}

/** Eén antwoord zoals het asiel het teruggeeft: het label van de vraag en wat er is ingevuld. */
export interface AfstandOpgaveAntwoord {
  veldSleutel: string;
  label: string;
  verplicht: boolean;
  waarde: string | null;
}

/** Eén dier zoals het bij het asiel staat. */
export interface AfstandOpgaveDier {
  id: number;
  naam: string | null;
  diersoortId: number | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  geboortedatum: string | null;
  chipPasnummer: string | null;
}

/**
 * Wat de eigenaar heeft ingediend, zoals het asiel het nu heeft.
 *
 * <p>Rechtstreeks daarvandaan en niet uit een kopie hier: het asiel kan de aanvraag verplaatsen of de
 * status wijzigen, en een kopie zou daarna stil afwijken van wat er echt staat.
 */
export interface AfstandOpgave {
  status: string;
  ingediendOp: string | null;
  vestigingNaam: string | null;
  contact: AfstandContact | null;
  dieren: AfstandOpgaveDier[];
  antwoorden: {
    dierId: number;
    dierNaam: string | null;
    antwoorden: AfstandOpgaveAntwoord[];
  }[];
  entingen: {
    dierId: number;
    dierNaam: string | null;
    regels: { vaccin: string | null; datum: string | null; geldigTot: string | null }[];
  }[];
}

export async function haalOpgave(aanvraagId: string, token: string): Promise<AfstandOpgave | null> {
  const result = await dataProvider.custom!<AfstandOpgave>({
    url: `public/afstand/aanvraag/${aanvraagId}/opgave`,
    method: "get",
    headers: sessie(token),
  });
  return result.data ?? null;
}

/**
 * De contactgegevens van een lopende aanvraag bijwerken.
 *
 * <p>Voor wie zijn dossier terugziet en er een tikfout in vindt: hij hoeft dan niet het hele
 * formulier opnieuw te doorlopen.
 */
export async function bewaarContact(
  aanvraagId: string,
  token: string,
  contact: AfstandContact,
): Promise<void> {
  await dataProvider.custom!({
    url: `public/afstand/aanvraag/${aanvraagId}/contact`,
    method: "put",
    headers: sessie(token),
    payload: contact,
  });
}
